import React, { useState, useCallback, useEffect } from 'react';
import { Tag, Empty } from 'antd';
import Radio from '@material-ui/core/Radio';
import { useHistory, useLocation } from 'react-router-dom';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import qs from 'qs';
import EmailLogo from './Email.svg';
import { CStandardInput } from '../../../component/Input';
import CButton from '../../../component/Button';
import InnerHeader from '../../../component/InnerHeader';
import Modal from '../../../component/Modal/Modal';
import {
  fetchDomainsAction,
  mdelloActivateAction,
  gsuiteActivateAction,
  deactivateAction,
  activationSuccess
} from '../../../actions/emailActivationAction';
import { useNotification } from '../../../providers/notificationsProvider';

const useStyles = makeStyles({
  filterDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '35px'
  },
  img: {
    height: '32px',
    width: '32px'
  },
  infoDiv: {
    alignItems: 'center',
    display: 'flex'
  },
  inputRoot: {
    backgroundColor: '#F0F4FF',
    borderRadius: '10px',
    fontSize: '12px',
    height: '40px',
    marginBottom: '20px',
    paddingLeft: '15px',
    width: '136px'
  },
  lastParentDiv: {
    alignItems: 'center',
    borderBottom: '1px solid #F0F4FF',
    borderTop: '1px solid #F0F4FF',
    display: 'flex',
    height: '88px',
    justifyContent: 'space-between'
  },
  layoutDiv: {
    height: 'calc(100vh - 155px)',
    overflowY: 'scroll'
  },
  managed: {
    marginLeft: '25px',
    textAlign: 'start',
    width: '130px'
  },
  name: {
    marginLeft: '28px',
    textAlign: 'start',
    width: '121px'
  },
  parentDiv: {
    alignItems: 'center',
    borderTop: '1px solid #F0F4FF',
    display: 'flex',
    height: '88px',
    justifyContent: 'space-between'
  },
  tag: {
    alignItems: 'center',
    background: 'white',
    border: '1px solid #CAD8FF',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    fontFamily: 'Poppins',
    fontSize: '14px',
    height: '40px',
    justifyContent: 'center',
    margin: '0px',
    padding: '13px 18px'
  },
  wrapperDiv: {
    padding: '0px 40px 0px 35px'
  }
});

const Email = () => {
  const [radioValue, setRadioValue] = useState('');
  const [domainId, setDomainId] = useState('');
  const [managedBy, setManagedBy] = useState('');
  const [isSaveEnabled, setSaveEnabled] = useState(true);

  const history = useHistory();
  const urlLocation = useLocation();

  const currentProject = useSelector(({ project }) => project.currentProject);
  const domains = useSelector(({ emailActivation }) => emailActivation.domains);
  const success = useSelector(({ emailActivation }) => emailActivation.success);

  const { notify } = useNotification();
  const classes = useStyles();
  const dispatch = useDispatch();

  const getSearchString = useCallback(() => {
    const { search } = qs.parse(urlLocation.search, {
      ignoreQueryPrefix: true
    });

    return search || '';
  }, [urlLocation.search]);

  const onSearch = (searchTerm) => {
    const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

    params.search = searchTerm;

    history.push({
      pathname: urlLocation.pathname,
      search: qs.stringify(params)
    });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaveEnabled(false);
    activate();
    setDomainId('');
  };

  const onCancel = () => {
    dispatch(activationSuccess(false));
    setDomainId('');
  };

  const activeDomains = domains?.map((item) => {
    if (item.status === 'active') {
      return item._id;
    }
    return '';
  });

  async function activate() {
    switch (radioValue) {
      case 'gsuite':
        dispatch(
          gsuiteActivateAction(
            domainId,
            () => {
              dispatch(fetchDomainsAction());
            },
            (message, severity) => notify({ message, severity })
          )
        );
        break;
      case 'mdello':
        dispatch(
          mdelloActivateAction(
            domainId,
            () => {
              dispatch(fetchDomainsAction());
            },
            (message, severity) => notify({ message, severity })
          )
        );
        break;
      case 'off':
        dispatch(
          deactivateAction(
            domainId,
            () => {
              dispatch(fetchDomainsAction());
            },
            (message, severity) => notify({ message, severity })
          )
        );
        break;
      default:
        return dispatch(fetchDomainsAction());
    }
  }

  useEffect(() => {
    async function load() {
      dispatch(fetchDomainsAction());
    }

    if (currentProject) {
      load();
    }
  }, [dispatch, currentProject]);

  const onTagClick = (emailManagedBy, _id) => {
    setSaveEnabled(true);
    dispatch(activationSuccess(true));
    setManagedBy(emailManagedBy || 'off');
    setDomainId(_id);
  };

  return (
    <div className={classes.layoutDiv}>
      <InnerHeader subtitle={domains?.length} title="Email" />
      <div className={classes.filterDiv}>
        <CStandardInput
          classes={{ root: classes.inputRoot }}
          disableUnderline
          htmlFor="standard-input-table"
          id="standard-input-table"
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Search"
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
          value={getSearchString()}
        />
      </div>

      <div className={classes.wrapperDiv}>
        {domains?.filter((item) => item.name.toLowerCase().includes(getSearchString()))[0] ? (
          domains
            ?.filter((item) => item.name.toLowerCase().includes(getSearchString()))
            .map(({ name, _id, emailManagedBy }, index) => {
              return (
                <div
                  key={index}
                  className={
                    index === domains?.length - 1 ? classes.lastParentDiv : classes.parentDiv
                  }
                >
                  <div className={classes.infoDiv}>
                    <img alt="emailLogo" className={classes.img} src={EmailLogo} />
                    <div className={classes.name}>{name}</div>
                  </div>
                  <div className={classes.managed}>{emailManagedBy || 'off'}</div>
                  <Tag className={classes.tag} onClick={() => onTagClick(emailManagedBy, _id)}>
                    Activate
                  </Tag>
                </div>
              );
            })
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>

      <Modal
        footerActions={
          <>
            <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton
              disabled={!isSaveEnabled || !activeDomains?.includes(domainId)}
              onClick={(e) => onSave(e)}
              type="primary"
              xstyle={{ height: '40px' }}
            >
              Save
            </CButton>
          </>
        }
        handleClose={onCancel}
        isModalOpen={success}
        styles={{
          '& .MuiDialogContent-root': {
            paddingTop: '0px'
          },
          width: '330px'
        }}
        titleContent={<p className="modal-title">Activate Email</p>}
      >
        <FormControl>
          <RadioGroup defaultValue={managedBy} onChange={(e) => setRadioValue(e.target.value)}>
            <FormControlLabel
              control={<Radio color="primary" />}
              disabled={!activeDomains?.includes(domainId)}
              label="Off"
              value="off"
            />
            <FormControlLabel
              control={<Radio color="primary" />}
              disabled={!activeDomains?.includes(domainId)}
              label="GSuite"
              value="gsuite"
            />
            <FormControlLabel
              control={<Radio color="primary" />}
              disabled={!activeDomains?.includes(domainId)}
              label="Mdello"
              value="mdello"
            />
          </RadioGroup>
        </FormControl>
      </Modal>
    </div>
  );
};

export default Email;
